/** @jsx jsx */

import { jsx } from '@emotion/core';

import { TextField as MuiTextField } from '@material-ui/core';
import { TextFieldProps as MuiTextFieldProps } from '@material-ui/core/TextField';
import { InputProps as StandardInputProps } from '@material-ui/core/Input';
import { pick, mergeLeft, omit } from 'ramda';

import MaskedInput, {
  propNames as maskPropNames,
  MaskedProps,
} from './MaskedInput';

import React from 'react';

import styles from './styles';

export type TextFieldProps = MuiTextFieldProps & MaskedProps;

const defaultProps: TextFieldProps = {
  required: true,
  fullWidth: true,
  margin: 'normal',
  variant: 'filled',
};

/**
 * TextField with default props and stylings.
 *
 * Also support masking.
 */
const TextField: React.ForwardRefExoticComponent<
  TextFieldProps
> = React.forwardRef((props, ref) => {
  // Setting `value=null` causes warnings, but we have null values in our state.
  // So, for the sake of convenience, if it's set specifically to null, we'll
  // cast that to an empty string.
  const { required } = props;
  if ('value' in props && props.value === null) {
    props = { ...props, value: '' };
  }

  props = mergeLeft(props, defaultProps);

  const maskProps: MaskedProps = pick(maskPropNames, props);
  const maksedInputProps = props.mask
    ? {
        inputComponent: MaskedInput,
        inputProps: {
          ...props.inputProps,
          ...maskProps,
        },
      }
    : {};

  const InputProps = {
    ...maksedInputProps,
    disableUnderline: true,
    ...(props.InputProps || {}),
  } as Partial<StandardInputProps>;

  return (
    <MuiTextField
      required={required}
      InputLabelProps={{ required: false }} // Avoid MUI from adding an asterisk to the label.
      css={styles.textField}
      ref={ref}
      {...omit(maskPropNames, props)}
      InputProps={InputProps}
    />
  );
});

export default TextField;
